/**
 * ChauparBoard — Cross-shaped board component
 *
 * 15×15 CSS Grid rendering the Chaupar board with:
 * - 4 arms extending from central Charkoni
 * - Royal/ornate theme (gold, deep purple, rich colors)
 * - No safe squares (unlike Pachisi)
 * - Eliminated pawns in graveyard area
 * - Block indicators (shield icon)
 * - Move highlighting
 */

import React, { useMemo } from 'react';
import {
  ChauparGameState,
  ChauparMove,
  DiceResult,
  PawnAnimation,
  PlayerColor,
  PawnPosition,
  PLAYER_COLORS,
  TRACK_LENGTH,
  PLAYER_START_POSITIONS,
  TEAMS,
} from '../types';
import { ChauparEngine } from '../ChauparEngine';
import './ChauparBoard.css';

interface ChauparBoardProps {
  gameState: ChauparGameState;
  currentPlayer: PlayerColor;
  validMoves: ChauparMove[];
  diceResult: DiceResult | null;
  onPawnSelect: (player: PlayerColor, pawnIndex: number) => void;
  onSquareClick?: (position: number) => void;
  isPlayerTurn: boolean;
  selectedPawn: { player: PlayerColor; pawnIndex: number } | null;
  animatingMove: PawnAnimation | null;
}

// Board layout: 15x15 grid
// The cross shape has 4 arms of 3 columns × 6 rows each
// Center is 3×3 Charkoni
const BOARD_SIZE = 15;

// Map track positions (0-67) to grid coordinates
const getTrackGridPosition = (trackPos: number): { row: number; col: number } => {
  // Top arm (positions going down): columns 6,7,8 rows 0-5
  // Right arm (positions going right): rows 6,7,8 columns 9-14
  // Bottom arm (positions going up): columns 6,7,8 rows 9-14
  // Left arm (positions going left): rows 6,7,8 columns 0-5

  // Simplified mapping for the 68-square track
  // Each arm has 17 squares (3 lanes × ~6 positions)
  const arm = Math.floor(trackPos / 17);
  const posInArm = trackPos % 17;

  switch (arm) {
    case 0: // Red arm (top) - going clockwise
      if (posInArm < 6) return { row: 5 - posInArm, col: 6 };
      if (posInArm < 8) return { row: 0, col: 6 + (posInArm - 5) };
      if (posInArm < 14) return { row: posInArm - 8, col: 8 };
      return { row: 6, col: 8 + (posInArm - 13) };
    case 1: // Blue arm (right)
      if (posInArm < 6) return { row: 6, col: 9 + posInArm };
      if (posInArm < 8) return { row: 6 + (posInArm - 5), col: 14 };
      if (posInArm < 14) return { row: 8, col: 14 - (posInArm - 8) };
      return { row: 8 + (posInArm - 13), col: 6 };
    case 2: // Green arm (bottom)
      if (posInArm < 6) return { row: 9 + posInArm, col: 8 };
      if (posInArm < 8) return { row: 14, col: 8 - (posInArm - 5) };
      if (posInArm < 14) return { row: 14 - (posInArm - 8), col: 6 };
      return { row: 8, col: 6 - (posInArm - 13) };
    case 3: // Yellow arm (left)
      if (posInArm < 6) return { row: 8, col: 5 - posInArm };
      if (posInArm < 8) return { row: 8 - (posInArm - 5), col: 0 };
      if (posInArm < 14) return { row: 6, col: posInArm - 8 };
      return { row: 6 - (posInArm - 13), col: 8 };
    default:
      return { row: 7, col: 7 };
  }
};

export const ChauparBoard: React.FC<ChauparBoardProps> = ({
  gameState,
  currentPlayer,
  validMoves,
  diceResult,
  onPawnSelect,
  isPlayerTurn,
  selectedPawn,
  animatingMove,
}) => {
  // Determine which squares are part of the board
  const boardSquares = useMemo(() => {
    const squares: { row: number; col: number; type: string; playerArm?: PlayerColor }[] = [];

    // Center Charkoni (3×3)
    for (let r = 6; r <= 8; r++) {
      for (let c = 6; c <= 8; c++) {
        squares.push({ row: r, col: c, type: 'charkoni' });
      }
    }

    // Top arm (rows 0-5, cols 6-8)
    for (let r = 0; r <= 5; r++) {
      for (let c = 6; c <= 8; c++) {
        squares.push({ row: r, col: c, type: 'arm', playerArm: 'red' });
      }
    }

    // Right arm (rows 6-8, cols 9-14)
    for (let r = 6; r <= 8; r++) {
      for (let c = 9; c <= 14; c++) {
        squares.push({ row: r, col: c, type: 'arm', playerArm: 'blue' });
      }
    }

    // Bottom arm (rows 9-14, cols 6-8)
    for (let r = 9; r <= 14; r++) {
      for (let c = 6; c <= 8; c++) {
        squares.push({ row: r, col: c, type: 'arm', playerArm: 'green' });
      }
    }

    // Left arm (rows 6-8, cols 0-5)
    for (let r = 6; r <= 8; r++) {
      for (let c = 0; c <= 5; c++) {
        squares.push({ row: r, col: c, type: 'arm', playerArm: 'yellow' });
      }
    }

    return squares;
  }, []);

  // Get pawns at each position for rendering
  const getPawnsAtSquare = (row: number, col: number) => {
    const pawns: { player: PlayerColor; pawnIndex: number }[] = [];

    for (const color of PLAYER_COLORS) {
      for (const pawn of gameState.pawns[color]) {
        if (pawn.location === 'track' && pawn.trackPosition !== undefined) {
          const gridPos = getTrackGridPosition(pawn.trackPosition);
          if (gridPos.row === row && gridPos.col === col) {
            pawns.push({ player: color, pawnIndex: pawn.pawnIndex });
          }
        }
      }
    }

    return pawns;
  };

  // Get pawns that have reached home (shown in Charkoni area)
  const getHomePawns = () => {
    const pawns: { player: PlayerColor; pawnIndex: number }[] = [];
    for (const color of PLAYER_COLORS) {
      for (const pawn of gameState.pawns[color]) {
        if (pawn.location === 'home') {
          pawns.push({ player: color, pawnIndex: pawn.pawnIndex });
        }
      }
    }
    return pawns;
  };

  // Get eliminated pawns
  const getEliminatedPawns = () => {
    const pawns: { player: PlayerColor; pawnIndex: number }[] = [];
    for (const color of PLAYER_COLORS) {
      for (const pawn of gameState.pawns[color]) {
        if (pawn.location === 'eliminated') {
          pawns.push({ player: color, pawnIndex: pawn.pawnIndex });
        }
      }
    }
    return pawns;
  };

  // Check if a pawn has valid moves
  const pawnHasValidMoves = (player: PlayerColor, pawnIndex: number) => {
    return validMoves.some(m => m.player === player && m.pawnIndex === pawnIndex && m.action !== 'skip');
  };

  // Check if square has a block
  const squareHasBlock = (row: number, col: number) => {
    const pawns = getPawnsAtSquare(row, col);
    if (pawns.length < 2) return false;

    for (let i = 0; i < pawns.length; i++) {
      for (let j = i + 1; j < pawns.length; j++) {
        const teamI = TEAMS.teamA.includes(pawns[i].player) ? 'teamA' : 'teamB';
        const teamJ = TEAMS.teamA.includes(pawns[j].player) ? 'teamA' : 'teamB';
        if (teamI === teamJ) return true;
      }
    }
    return false;
  };

  const homePawns = getHomePawns();
  const eliminatedPawns = getEliminatedPawns();

  return (
    <div className="chaupar-board-container" role="region" aria-label="Chaupar game board">
      {/* Turn indicator */}
      <div className={`chaupar-turn-indicator chaupar-turn-${currentPlayer}`} aria-live="polite">
        <span className="turn-dot" />
        <span className="turn-text">
          {currentPlayer.charAt(0).toUpperCase() + currentPlayer.slice(1)}'s Turn
        </span>
      </div>

      {/* Main board grid */}
      <div className="chaupar-board" role="grid" aria-label="Chaupar board grid">
        {boardSquares.map(({ row, col, type, playerArm }) => {
          const pawnsHere = getPawnsAtSquare(row, col);
          const hasBlock = squareHasBlock(row, col);
          const isCharkoni = type === 'charkoni';

          // Find track position for this cell (reverse lookup)
          let trackPosAtCell: number | undefined;
          for (let tp = 0; tp < 68; tp++) {
            const gp = getTrackGridPosition(tp);
            if (gp.row === row && gp.col === col) {
              trackPosAtCell = tp;
              break;
            }
          }

          return (
            <div
              key={`${row}-${col}`}
              className={`chaupar-square ${type} ${playerArm ? `arm-${playerArm}` : ''} ${hasBlock ? 'has-block' : ''}`}
              style={{ gridRow: row + 1, gridColumn: col + 1 }}
              role="gridcell"
              aria-label={`Square at row ${row}, column ${col}${isCharkoni ? ' (Charkoni)' : ''}${hasBlock ? ' (blocked)' : ''}`}
            >
              {/* Position number for track squares */}
              {trackPosAtCell !== undefined && (
                <span className="chaupar-pos-number" aria-hidden="true">{trackPosAtCell}</span>
              )}
              {/* Render pawns on this square */}
              {pawnsHere.map(({ player, pawnIndex }) => {
                const hasValidMove = pawnHasValidMoves(player, pawnIndex);
                const isSelected = selectedPawn?.player === player && selectedPawn?.pawnIndex === pawnIndex;

                return (
                  <button
                    key={`${player}-${pawnIndex}`}
                    className={`chaupar-pawn pawn-${player} ${hasValidMove ? 'has-moves' : ''} ${isSelected ? 'selected' : ''}`}
                    onClick={() => isPlayerTurn && hasValidMove && onPawnSelect(player, pawnIndex)}
                    disabled={!isPlayerTurn || !hasValidMove}
                    aria-label={`${player} pawn ${pawnIndex + 1}${hasValidMove ? ' (can move)' : ''}`}
                    tabIndex={hasValidMove ? 0 : -1}
                  >
                    <span className="pawn-icon">●</span>
                    {pawnIndex + 1}
                  </button>
                );
              })}

              {/* Block indicator */}
              {hasBlock && <span className="block-indicator" aria-label="Block">🛡️</span>}
            </div>
          );
        })}

        {/* Charkoni — Home area for completed pawns */}
        <div className="charkoni-area" style={{ gridRow: '7 / 10', gridColumn: '7 / 10' }}>
          <div className="charkoni-label">Home</div>
          <div className="charkoni-pawns">
            {homePawns.map(({ player, pawnIndex }) => (
              <span
                key={`home-${player}-${pawnIndex}`}
                className={`chaupar-pawn pawn-${player} in-charkoni`}
                aria-label={`${player} pawn ${pawnIndex + 1} home`}
              >
                <span className="pawn-icon">●</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Graveyard — eliminated pawns */}
      {eliminatedPawns.length > 0 && (
        <div className="chaupar-graveyard" aria-label="Eliminated pawns">
          <h4 className="graveyard-title">☠️ Eliminated</h4>
          <div className="graveyard-pawns">
            {eliminatedPawns.map(({ player, pawnIndex }) => (
              <span
                key={`elim-${player}-${pawnIndex}`}
                className={`eliminated-pawn pawn-${player}`}
                aria-label={`${player} pawn ${pawnIndex + 1} eliminated`}
              >
                ●
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Team scores */}
      <div className="chaupar-team-scores">
        <div className="team-score team-a">
          <span className="team-label">Team A (Red + Green)</span>
          <span className="team-home">🏠 {gameState.pawnsHome.red + gameState.pawnsHome.green}/8</span>
        </div>
        <div className="team-score team-b">
          <span className="team-label">Team B (Blue + Yellow)</span>
          <span className="team-home">🏠 {gameState.pawnsHome.blue + gameState.pawnsHome.yellow}/8</span>
        </div>
      </div>
    </div>
  );
};

export default ChauparBoard;
